import { cancelEntryOutside } from "../core/entry-intent";
import { Suspense, lazy, useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { ArrowLeft, UserRound, Ticket, WifiOff, Download } from "lucide-react";
import { useStore, sessionExpired } from "../core/store";
import { config } from "../core/config";
import { Button, Loading } from "./ui";
import { friendlyError } from "../core/i18n";
import { track } from "../core/telemetry";
const MockControls = lazy(() => import("./MockControls"));
interface InstallEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<unknown>;
}
export default function Layout() {
  const { t, i18n } = useTranslation();
  const location = useLocation(),
    navigate = useNavigate();
  const ready = useStore((s) => s.ready),
    profile = useStore((s) => s.profile),
    context = useStore((s) => s.context),
    notice = useStore((s) => s.notice);
  const [online, setOnline] = useState(navigator.onLine);
  const [update, setUpdate] = useState<(() => void) | null>(null);
  const [install, setInstall] = useState<InstallEvent | null>(null);
  const [installHelp, setInstallHelp] = useState(false);
  useEffect(() => {
    void useStore.getState().init();
    const unauthorized = () => sessionExpired();
    const refresh = () => {
      void useStore
        .getState()
        .refresh()
        .catch(() => {});
    };
    const net = () => {
      setOnline(navigator.onLine);
      if (navigator.onLine) refresh();
    };
    const viewport = () => {
      const v = window.visualViewport;
      document.documentElement.style.setProperty(
        "--viewport-height",
        `${v?.height || window.innerHeight}px`,
      );
      document.documentElement.dataset.keyboard = String(
        window.innerHeight - (v?.height || window.innerHeight) > 150,
      );
    };
    window.addEventListener("fz:unauthorized", unauthorized);
    window.addEventListener("fz:mock-refresh", refresh);
    window.addEventListener("online", net);
    window.addEventListener("offline", net);
    window.visualViewport?.addEventListener("resize", viewport);
    window.addEventListener("orientationchange", viewport);
    const sw = (event: MessageEvent) => {
      if (event.data?.type === "TICKET_UPDATE") refresh();
    };
    navigator.serviceWorker?.addEventListener("message", sw);
    viewport();
    return () => {
      window.removeEventListener("fz:unauthorized", unauthorized);
      window.removeEventListener("fz:mock-refresh", refresh);
      window.removeEventListener("online", net);
      window.removeEventListener("offline", net);
      window.visualViewport?.removeEventListener("resize", viewport);
      window.removeEventListener("orientationchange", viewport);
      navigator.serviceWorker?.removeEventListener("message", sw);
    };
  }, []);
  useEffect(() => {
    if (!import.meta.env.PROD) return;
    let timer: ReturnType<typeof setInterval> | undefined;
    let disposed = false;
    void import("virtual:pwa-register").then(({ registerSW }) => {
      if (disposed) return;
      const updateSW = registerSW({
        onNeedRefresh() {
          setUpdate(() => () => void updateSW(true));
        },
        onRegisteredSW(_url, r) {
          if (r && !disposed) {
            void r.update().catch(() => {});
            timer = setInterval(
              () => void r.update().catch(() => {}),
              21600000,
            );
          }
        },
      });
    });
    return () => {
      disposed = true;
      clearInterval(timer);
    };
  }, []);
  useEffect(() => {
    const fn = (e: Event) => {
      e.preventDefault();
      setInstall(e as InstallEvent);
    };
    window.addEventListener("beforeinstallprompt", fn);
    return () => window.removeEventListener("beforeinstallprompt", fn);
  }, []);
  useEffect(() => {
    cancelEntryOutside(location.pathname);
    window.scrollTo(0, 0);
    track("route" + location.pathname.replace(/\/in\/.+/, "/in"));
    document.title = `Filazero · ${t("ui.tagline")}`;
  }, [location.pathname, t]);
  useEffect(() => {
    const color = context?.color;
    if (color && /^#[0-9a-f]{6}$/i.test(color)) {
      document.documentElement.style.setProperty("--brand", color);
      const n = parseInt(color.slice(1), 16);
      const channels = [n >> 16, (n >> 8) & 255, n & 255].map((c) => {
        const v = c / 255;
        return v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
      });
      const lum =
        0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2];
      document.documentElement.style.setProperty(
        "--brand-ink",
        lum > 0.18 ? "#53247a" : color,
      );
      document.documentElement.style.setProperty(
        "--on-brand",
        lum > 0.18 ? "#171321" : "#ffffff",
      );
    }
  }, [context?.color]);
  return (
    <>
      <a className="skip-link" href="#main">
        {t("ui.skip")}
      </a>
      <div className="app-shell">
        <header className="topbar">
          <Link to="/start" className="brand-link" aria-label="Filazero">
            <img
              src="/assets/images/fz-logo-default.svg"
              alt="Filazero"
              width="119"
              height="32"
            />
          </Link>
          <div className="top-tools">
            <select
              className="language-select"
              aria-label={t("ui.language")}
              value={i18n.language}
              onChange={(e) => void i18n.changeLanguage(e.target.value)}
            >
              <option value="pt-BR">PT</option>
              <option value="es-CL">ES</option>
              <option value="en-US">EN</option>
            </select>
            <Link
              className="icon-button"
              to={profile ? "/profile" : "/login"}
              aria-label={t("ui.profile")}
            >
              <UserRound size={21} />
            </Link>
          </div>
        </header>
        {config.mock && (
          <Suspense>
            <MockControls />
          </Suspense>
        )}
        {!online && (
          <div className="notice warning" role="status">
            <WifiOff size={20} />
            <p>{t("ui.offline")}</p>
          </div>
        )}
        {notice && (
          <div className="notice" role="status">
            <p>{friendlyError(notice)}</p>
            <button
              className="icon-button"
              aria-label={t("ui.close")}
              onClick={() => useStore.setState({ notice: null })}
            >
              ×
            </button>
          </div>
        )}
        {location.pathname !== "/start" && (
          <nav className="crumb-nav" aria-label={t("ui.navigation")}>
            <button
              className="back-button"
              onClick={() => {
                if (location.key === "default") navigate("/start");
                else navigate(-1);
              }}
            >
              <ArrowLeft size={18} />
              {t("ui.back")}
            </button>
            {profile && (
              <NavLink to="/home">
                <Ticket size={16} />
                {t("ui.home")}
              </NavLink>
            )}
          </nav>
        )}
        <main id="main">
          {ready ? (
            <Suspense fallback={<Loading />}>
              <Outlet />
            </Suspense>
          ) : (
            <Loading />
          )}
        </main>
        <footer className="brand-footer">
          <span>{t("ui.tagline")}</span>
          <button
            className="text-button install"
            onClick={() => {
              if (install) void install.prompt();
              else setInstallHelp(!installHelp);
            }}
          >
            <Download size={14} />
            {t("ui.install")}
          </button>
          {installHelp && <p>{t("ui.installHelp")}</p>}
        </footer>
        {update && (
          <div className="update-banner" role="status">
            <span>{t("ui.update")}</span>
            <Button onClick={update}>{t("ui.updateAction")}</Button>
          </div>
        )}
      </div>
    </>
  );
}
